import { Flex, Text, useDisclosure, Tooltip } from "@chakra-ui/react";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { BaseTableFilters } from "@/components/BaseTableFilters/BaseTableFilters";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../../components/Layout/Layout";
import { useEffect, useMemo, useState } from "react";
import { fetchCustomers } from "../../store/slices/customers";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { formatDate } from "../../helpers/formatDate";
import { Pagination } from "../../components/Pagination/Pagination";
import { useSorting } from "../../hooks/useSorting";
import { Customer } from "@/model/customers";
import api from "../../api";
import { Recruiter } from "@/model/recruiters";
import { CustomersPopupFilter } from "./components/CustomersPopupFilter";
import { CustomersFilter, defaultCustomersFilterFields } from "./_helpers";
import { toUnix } from "@/utils/date";
import { getOffset } from "@/utils/pagination";
import { useSessionStorageFilters } from "@/hooks/useSessionStorageFilters";

const columnHelper = createColumnHelper<Customer>();

export const CustomersPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();

  const { customers, isLoading } = useAppSelector((state: RootState) => state.customers);
  const { guides } = useAppSelector((state: RootState) => state.guides);
  const [recruiters, setRecruiters] = useState<Recruiter[]>([]);

  const handleCreate = () => {
    navigate("create");
  };

  const [totalItems, setTotalItems] = useState(0);

  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 20;

  const { sortColumn, sortDirection, handleSort } = useSorting("customer.created_at");

  // Используем универсальный хук для работы с фильтрами
  const { formik, handleSearch, handleApplyFilters, handleResetFilters } = useSessionStorageFilters<CustomersFilter>({
    storageKey: "customersPageFilters",
    defaultValues: defaultCustomersFilterFields,
  });

  useEffect(() => {
    const offset = getOffset(page, limit);
    const { created_at_from, created_at_to, ...rest } = formik.values;
    const filters = {
      ...rest,
      created_at_from: toUnix(created_at_from),
      created_at_to: toUnix(created_at_to),
      sort_column: sortColumn || undefined,
      sort_direction: sortDirection || undefined,
      limit: String(limit),
      offset: String(offset),
    };
    dispatch(fetchCustomers({ params: { filters } })).then((res: any) => {
      setTotalItems(res.payload.count);
    });
  }, [dispatch, page, limit, formik.values, sortColumn, sortDirection]);

  useEffect(() => {
    let isMounted = true;
    api.recruiters
      .recruiters({ customer: true })
      .then(({ data }) => {
        if (isMounted) setRecruiters(data || []);
      })
      .catch(() => {
        if (isMounted) setRecruiters([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (formik.values.business_area) count += 1;
    if (formik.values.responsible_id) count += 1;
    if (formik.values.created_at_from) count += 1;
    if (formik.values.created_at_to) count += 1;
    return count;
  }, [formik.values.business_area, formik.values.responsible_id, formik.values.created_at_from, formik.values.created_at_to]);

  const businessAreaOptions = useMemo(() => {
    const list: any = guides?.BusinessArea;
    if (!list) return [] as { value: string; label: string }[];
    const options: { value: string; label: string }[] = [];
    for (const key in list) {
      if (Object.hasOwnProperty.call(list, key)) {
        const val = String(list[key]);
        options.push({ value: key, label: val });
      }
    }
    return options;
  }, [guides]);

  const handleOpenFilters = () => {
    onFiltersOpen();
  };

  const columns = useMemo<Array<ColumnDef<Customer, any>>>(() => {
    const baseColumns: Array<ColumnDef<Customer, any>> = [
      columnHelper.accessor("name", {
        header: "Название",
        cell: (info) => (
          <Text fontWeight={600} color="#3b515e" fontSize={14}>
            {info.getValue()}
          </Text>
        ),
        enableColumnFilter: true,
        size: 160,
        minSize: 120,
      }),
      columnHelper.accessor("business_area.name", {
        header: "Сфера деятельности",
        cell: (info) => (
          <Text w="max-content" fontSize={12} borderRadius="9999px" padding="2px 8px" border="1px solid #e1e7ef">
            {info.getValue()}
          </Text>
        ),
        enableColumnFilter: true,
        size: 160,
        minSize: 120,
      }),
      columnHelper.accessor("responsible_user.username", {
        header: "Ответственный аккаунт-менеджер",
        cell: (info) => (
          <Tooltip label={info.getValue() || ""} hasArrow>
            <Text noOfLines={1} maxW="160px">
              {info.getValue()}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
      }),
      columnHelper.accessor("created_at", {
        header: "Дата создания",
        cell: (info) => (
          <Tooltip label={formatDate(info.getValue() || "")} hasArrow>
            <Text noOfLines={1} maxW="160px">
              {formatDate(info.getValue() || "")}
            </Text>
          </Tooltip>
        ),
        enableColumnFilter: true,
        enableSorting: true,
      }),
    ];
    return baseColumns;
  }, []);

  return (
    <Layout>
      <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="24px">
        <Text fontSize={27} fontWeight={500}>
          Заказчики
        </Text>
        <BaseTableFilters
          handleSearch={handleSearch}
          placeHolderInput="Поиск по имени заказчика"
          searchValue={formik.values.search || ""}
          isShowBtnPopupFilters
          selectedFiltersCount={appliedFiltersCount}
          onClickBtnPopupFilters={handleOpenFilters}
          isOpenPopupFilters={isFiltersOpen}
          onClosePopupFilters={onFiltersClose}
          popupFilters={
            <CustomersPopupFilter
              values={formik.values}
              onApply={(v) => {
                handleApplyFilters(v);
                onFiltersClose();
              }}
              onReset={() => {
                formik.resetForm({ values: defaultCustomersFilterFields });

                onFiltersClose();
              }}
              businessAreaOptions={businessAreaOptions}
              recruiters={recruiters}
            />
          }
          isLoading={isLoading}
          isShowCreateButton={true}
          onClickCreate={handleCreate}
        />

        <BaseTable
          list={customers.items}
          columns={columns as Array<ColumnDef<Customer, any>>}
          sortHandler={({ id, direction }) => handleSort({ id, direction: direction ?? null })}
          sortState={{
            column: sortColumn || "",
            direction: sortDirection,
          }}
          isLoading={isLoading}
        />

        <Pagination
          totalItems={totalItems}
          isLoading={isLoading}
          onPageChange={(offset, limit) => {
            const params = new URLSearchParams(searchParams);
            params.set("page", String(offset / limit + 1));
            params.set("limit", String(limit));
            navigate(`?${params.toString()}`);
          }}
        />
      </Flex>
    </Layout>
  );
};
